import { createApi } from '@reduxjs/toolkit/query/react';
import {
  ClassData,
  ClassRequest,
  ClassesResponseData,
} from '../../Shared/types/classes';
import baseQuery from '../../Shared/API/config';

export const classesApi = createApi({
  reducerPath: 'classesApi',
  baseQuery,
  tagTypes: ['Class'],
  endpoints: builder => ({
    getClasses: builder.query<
      ClassesResponseData,
      {
        search?: string;
        levelId?: string;
        gradeId?: string;
        assigned?: boolean;
      }
    >({
      query: ({ search, levelId, gradeId, assigned }) => {
        let url = `/enrollment/classes`;
        const params = new URLSearchParams();
        if (search) {
          params.append('search', search);
        }
        if (levelId) {
          params.append('levelId', levelId);
        }
        if (gradeId) {
          params.append('gradeId', gradeId);
        }
        if (assigned !== undefined) {
          params.append('assigned', String(assigned));
        }
        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        return url;
      },
      providesTags: ['Class'],
    }),
    getClass: builder.query<any, { id: string }>({
      query: ({ id }) => `/enrollment/classes/${id}`,
      providesTags: ['Class'],
    }),
    createClass: builder.mutation<ClassData, ClassRequest>({
      query: newClass => ({
        url: '/enrollment/classes',
        method: 'POST',
        body: newClass,
      }),
      invalidatesTags: ['Class'],
    }),
    updateClass: builder.mutation<
      ClassData,
      { id: number; updates: ClassRequest }
    >({
      query: ({ id, updates }) => ({
        url: `/enrollment/classes/${id}`,
        method: 'PUT',
        body: updates,
      }),
      async onQueryStarted({ id, updates }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          classesApi.util.updateQueryData(
            'getClasses',
            { search: '', levelId: '', gradeId: '' },
            draft => {
              const classesArray = draft as any;
              const classToUpdate = classesArray.find(
                (cls: any) => cls.id === id
              );
              if (classToUpdate) {
                Object.assign(classToUpdate, updates);
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_, __, { id }) => [{ type: 'Class', id }, 'Class'],
    }),
    deleteClass: builder.mutation<void, number>({
      query: id => ({
        url: `/enrollment/classes/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [{ type: 'Class', id }, 'Class'],
    }),
    retireClass: builder.mutation<void, number>({
      query: id => ({
        url: `/enrollment/classes/${id}`,
        method: 'PATCH',
        body: { status: 'inactive' },
      }),
      invalidatesTags: (_, __, id) => [{ type: 'Class', id }, 'Class'],
    }),
    assignClassTeacher: builder.mutation<
      void,
      { id: number; teacherId: number }
    >({
      query: ({ id, teacherId }) => ({
        url: `/enrollment/classes/${id}`,
        method: 'PATCH',
        body: { teacher: { id: teacherId } },
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'Class', id }, 'Class'],
    }),
    graduateClass: builder.mutation<{ graduated: number }, number>({
      query: id => ({
        url: `/enrollment/classes/${id}/graduate`,
        method: 'POST',
      }),
      invalidatesTags: (_, __, id) => [{ type: 'Class', id }, 'Class'],
    }),
  }),
});

export const {
  useGetClassesQuery,
  useGetClassQuery,
  useCreateClassMutation,
  useUpdateClassMutation,
  useDeleteClassMutation,
  useRetireClassMutation,
  useAssignClassTeacherMutation,
  useGraduateClassMutation,
} = classesApi;
